// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components/macro"
import { darken } from "polished"
import { ToastContentProps } from "react-toastify"
import { Trans } from "@lingui/react"
import * as UserContextActions from "../user-context/actions"
import {
  AckNotificationRequest,
  Notification as NotificationProto,
  NotificationAction,
} from "../../generated/wisetime/user/notification/v4/notification_pb"
import { getDefaultLanguage } from "../../locales/i18nUtil"
import * as UserContextSlice from "../user-context/slice"
import { LinkUnderlineSharedStyles } from "./styles"

enum ActionsType {
  DISMISS = 1,
  PRIMARY = 2,
  MIXED = 3,
}

const Wrapper = styled.div`
  padding: 1rem 2rem 2rem;
  &:not(:first-child) {
    border-top: 1px solid ${({ theme }) => theme.colors.gainsboro};
  }
`

const NotificationParagraph = styled.p`
  line-height: 18px;
  margin-bottom: 1rem;
`

const Title = styled.h5`
  margin-bottom: 1.2rem;
`

const Body = styled.div`
  font-size: 1.2rem;
`

const Actions = styled.div<{ actionsType: ActionsType }>`
  display: flex;
  justify-content: ${({ actionsType }) => {
    if (actionsType === ActionsType.DISMISS) {
      return "flex-start"
    }
    if (actionsType === ActionsType.PRIMARY) {
      return "flex-end"
    }
    return "space-between"
  }};
`

const SharedButtonStyles = styled.button`
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 2px;
`

const DismissButton = styled(SharedButtonStyles)`
  ${LinkUnderlineSharedStyles};
  margin-right: 2rem;
  padding: 8px 0;
`

const PrimaryButton = styled(SharedButtonStyles)`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.indigo};
  padding: 8px 10px;
  transition: background-color 300ms ease;

  &:hover {
    background-color: ${({ theme }) => darken(0.1, theme.colors.indigo)};
  }
`

type Props = {
  notification: NotificationProto.AsObject,
} & Partial<ToastContentProps>

const Notification = ({
  notification,
  closeToast,
}: Props): React.ReactElement<Props> | null => {
  const dispatch = useDispatch()
  const currentSiteId = useSelector(UserContextSlice.selectCurrentSiteId)
  const localizedNotification = notification.localizedContentsList.find(content => content.locale === getDefaultLanguage())

  const getActionsType = (actionsList: NotificationAction[]): ActionsType => {
    const isSingleAction = actionsList.length === 1
    if (isSingleAction && actionsList[0] === NotificationAction.DISMISS) {
      return ActionsType.DISMISS
    }
    if (isSingleAction && actionsList[0] === NotificationAction.PRIMARY_ACTION) {
      return ActionsType.PRIMARY
    }
    return ActionsType.MIXED
  }

  const handleAckNotification = (action: NotificationAction): void => {
    dispatch(UserContextActions.ackNotification(
      new AckNotificationRequest().setNotificationId(notification.id).setAction(action)
    ))
    if (closeToast) {
      closeToast()
    }
  }

  useEffect(() => {
    // hide notification if siteId is different
    if (notification.siteId !== "" && notification.siteId !== currentSiteId && closeToast) {
      closeToast()
    }
  }, [currentSiteId])

  if (localizedNotification === undefined) {
    return null
  }
  const bodyLines = localizedNotification.body.split("\n")
  return (
    <Wrapper>
      <Title>{localizedNotification.title}</Title>
      <Body>
        {bodyLines.map((line, index) =>
          <NotificationParagraph key={index}>
            {line}
          </NotificationParagraph>
        )}
      </Body>
      <Actions actionsType={getActionsType(notification.actionsAvailableList)}>
        {notification.actionsAvailableList.map(action => {
          if (action === NotificationAction.PRIMARY_ACTION) {
            return (
              <PrimaryButton
                key={action}
                type="button"
                onClick={(): void => handleAckNotification(action)}
              >
                {localizedNotification.primaryActionLabel}
              </PrimaryButton>
            )
          }
          return (
            <DismissButton
              key={action}
              type="button"
              onClick={(): void => handleAckNotification(action)}
            >
              <Trans id="label_dismiss" message="Dismiss"/>
            </DismissButton>
          )
        })}
      </Actions>
    </Wrapper>
  )
}

export default Notification
